import type { ContextSource, InterviewConfig, Message, SessionLog, SessionSummary } from '../types';

const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

async function request<T>(path: string, token: string | undefined, init?: RequestInit): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export function sendChat(
  token: string | undefined,
  config: InterviewConfig,
  messages: Message[],
  sessionId: string,
) {
  return request<{ message: Message }>('/api/chat', token, {
    method: 'POST',
    body: JSON.stringify({ config, messages, sessionId }),
  });
}

export function completeSession(token: string | undefined, sessionId: string, config: InterviewConfig, messages: Message[], durationSeconds: number) {
  return request<{ score: number; feedback: string[] }>('/api/sessions/complete', token, {
    method: 'POST',
    body: JSON.stringify({ sessionId, config, messages, durationSeconds }),
  });
}

export function fetchSessions(token: string | undefined) {
  return request<{ sessions: SessionSummary[] }>('/api/sessions', token);
}

export function fetchSession(token: string | undefined, id: string) {
  return request<SessionLog>(`/api/sessions/${encodeURIComponent(id)}`, token);
}

export type AdminOverview = {
  range: { start: string; end: string };
  totals: { users: number; sessions: number; completed: number; minutes: number };
  modes: Array<{ mode: string; sessions: number }>;
  roles: Array<{ role: string; sessions: number }>;
  recent: SessionSummary[];
  journeys: Array<{
    userId: string;
    email: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    events: JourneyEvent[];
  }>;
};

export type JourneyEventName = 'app_opened' | 'practice_setup_viewed' | 'practice_launched' | 'practice_started'
  | 'context_created' | 'context_selected' | 'voice_connected' | 'first_answer_submitted'
  | 'session_completed' | 'session_log_viewed';

export type JourneyEvent = {
  id: string;
  eventName: JourneyEventName;
  sessionId: string | null;
  properties: Record<string, string | number | boolean>;
  occurredAt: string;
};

export function fetchAdminOverview(token: string | undefined, start: string, end: string) {
  const query = new URLSearchParams({ start, end });
  return request<AdminOverview>(`/api/admin/overview?${query}`, token);
}

export function recordJourneyEvent(
  token: string | undefined,
  eventName: JourneyEventName,
  options: { sessionId?: string; properties?: Record<string, string | number | boolean>; clientEventId?: string } = {},
) {
  return request<{ event: JourneyEvent; created: boolean }>('/api/journey-events', token, {
    method: 'POST',
    body: JSON.stringify({
      eventName,
      clientEventId: options.clientEventId || crypto.randomUUID(),
      sessionId: options.sessionId,
      properties: options.properties,
    }),
  });
}

export function deleteAccount(token: string | undefined) {
  return request<{ status: string }>('/api/account', token, { method: 'DELETE' });
}

export function fetchContextSources(token: string | undefined) {
  return request<{ contextSources: ContextSource[] }>('/api/context', token);
}

export function addTextContext(token: string | undefined, text: string) {
  return request<{ contextSource: ContextSource }>('/api/context/text', token, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export function addRepositoryContext(token: string | undefined, url: string) {
  return request<{ contextSource: ContextSource }>('/api/context/repositories', token, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

export function createContextUpload(token: string | undefined, file: File) {
  return request<{
    contextSource: ContextSource;
    upload: { url: string; method: string; headers: Record<string, string>; expiresAt: number };
  }>('/api/context/uploads', token, {
    method: 'POST',
    body: JSON.stringify({ fileName: file.name, contentType: file.type || undefined, sizeBytes: file.size }),
  });
}

export function completeContextUpload(token: string | undefined, id: string) {
  return request<{ contextSource: ContextSource }>(`/api/context/uploads/${id}/complete`, token, { method: 'POST' });
}
